import { useEffect, useRef, useState, useCallback } from "react";
import { MapPin, Loader2 } from "lucide-react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { api, apiError } from "../../lib/api";
import { toast } from "sonner";

export function InterventionsMap({ paramsStr }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/ambulances/map?${paramsStr}`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [paramsStr]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!elRef.current || mapRef.current) return;
    const map = L.map(elRef.current, { scrollWheelZoom: false }).setView([6.5, -4.5], 6);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 18, attribution: "© OpenStreetMap",
    }).addTo(map);
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);
    setTimeout(() => map.invalidateSize(), 200);
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current, layer = layerRef.current;
    if (!map || !layer || !data) return;
    layer.clearLayers();
    const maxTotal = Math.max(1, ...data.points.map((p) => p.total));
    const maxFlow = Math.max(1, ...data.flows.map((f) => f.count));

    data.flows.forEach((f) => {
      L.polyline([f.from_coord, f.to_coord], {
        color: "#0EA5E9", weight: 1 + (f.count / maxFlow) * 5, opacity: 0.35, dashArray: "4 6",
      }).bindTooltip(`${f.from} → ${f.to} · ${f.count} transfert(s)`, { sticky: true }).addTo(layer);
    });

    data.points.forEach((p) => {
      const r = 8 + (p.total / maxTotal) * 22;
      L.circleMarker([p.lat, p.lng], {
        radius: r, color: "#0A2540", weight: 1.5, fillColor: "#0EA5E9", fillOpacity: 0.55,
      }).bindTooltip(
        `<b>${p.commune}</b><br/>Origines : ${p.origine}<br/>Destinations : ${p.destination}<br/>Total : ${p.total}`,
        { direction: "top" }
      ).bindPopup(
        `<b>${p.commune}</b><br/>Origines : ${p.origine}<br/>Destinations : ${p.destination}<br/>Total : ${p.total}`
      ).addTo(layer);
    });

    const pts = data.points.map((p) => [p.lat, p.lng]);
    if (pts.length) map.fitBounds(pts, { padding: [40, 40], maxZoom: 11 });
    setTimeout(() => map.invalidateSize(), 100);
  }, [data]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="amb-map">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <MapPin size={16} className="text-ciel" />
        <h3 className="font-display text-sm font-semibold text-navy">Carte des interventions</h3>
        {loading && <Loader2 size={14} className="animate-spin text-slate-400" />}
      </div>
      <p className="mb-2 text-[11px] text-slate-500">
        {data ? `${data.count} intervention(s) affichée(s)` : "—"} · taille des cercles = volume · lignes = transferts origine → destination
      </p>
      <div ref={elRef} className="h-[420px] w-full overflow-hidden rounded-xl" data-testid="amb-map-canvas" style={{ zIndex: 0 }} />
      <div className="mt-3 flex flex-wrap items-center gap-4 text-[11px] text-slate-500">
        <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded-full" style={{ background: "#0EA5E9", opacity: 0.6, border: "1.5px solid #0A2540" }} />Commune desservie</span>
        <span className="flex items-center gap-1.5"><span className="inline-block h-0.5 w-6" style={{ background: "#0EA5E9" }} />Transfert inter-communes</span>
      </div>
    </div>
  );
}
